/**
 * The `timings` frame, read off a screener response.
 *
 * The server measures one request's stages and ships them as **one more named
 * frame** — `services/qv-data/app/qf/perf.py` builds it, and named framing is
 * why that needed no wire-format change and no version bump: a client that does
 * not know the frame ignores it, so an older build simply shows no panel.
 *
 * Pure, and in `lib/` for the reason everything here is: all of the behaviour is
 * edge cases — a missing frame, a frame with no rows, a total that disagrees
 * with its parts — and none of them needs a rendered component to state.
 */

import type { ScreenerFrame } from "@qufolio/transport";

/** The frame's name on the wire. Contract, shared with `perf.TIMINGS_FRAME`. */
export const TIMINGS_FRAME = "timings";

/** The row the server calls the outer bracket rather than a stage. */
export const TOTAL_STAGE = "total";

export interface ScreenerStage {
  name: string;
  ms: number;
  detail?: string;
}

export interface ScreenerTimings {
  /** The pipeline, in the order the server recorded it. */
  stages: ScreenerStage[];
  /** The outer bracket — what the whole request took. */
  totalMs: number;
  /**
   * `total` minus the summed stages.
   *
   * **Shown rather than hidden**, which is the server's stated rule: a total
   * derived from its parts would make an un-instrumented stage invisible, and
   * that is the thing worth finding. Never negative — clamped, because nested
   * spans (the universe rows sit inside `membership`) can sum past the bracket.
   */
  unattributedMs: number;
  /**
   * Did this request reuse the 15.7-million-row universe frame, or rebuild it?
   *
   * The one field worth reading first. A slow screen is almost always a
   * rebuild, and the group being screened has almost nothing to do with it —
   * measured, a six-symbol group took 8.0s while the Russell 2000 took 0.39s.
   */
  universe: "cached" | "rebuilt" | "unknown";
}

/**
 * Read the timings frame, or `undefined` when the server did not send one.
 *
 * Undefined rather than an empty record: "this server does not report timings"
 * and "this request took no time" are different facts, and a panel that showed
 * zeros for the first would be inventing a measurement.
 */
export function readScreenerTimings(
  frames: readonly ScreenerFrame[] | undefined,
): ScreenerTimings | undefined {
  const frame = frames?.find((f) => f.name === TIMINGS_FRAME);
  if (!frame || frame.rows.length === 0) return undefined;

  const stages: ScreenerStage[] = [];
  let totalMs = 0;
  for (const row of frame.rows) {
    const name = typeof row.name === "string" ? row.name : "";
    const ms = typeof row.ms === "number" && Number.isFinite(row.ms) ? row.ms : 0;
    const detail = typeof row.detail === "string" && row.detail ? row.detail : undefined;
    if (!name) continue;
    if (name === TOTAL_STAGE) {
      totalMs = ms;
      continue;
    }
    stages.push(detail ? { name, ms, detail } : { name, ms });
  }
  if (stages.length === 0 && totalMs === 0) return undefined;

  // The universe rows are the diagnosis, so they are lifted out of the list
  // rather than left for a reader to spot among a dozen names.
  const universeRow = stages.find((s) => s.name === "universe");
  const universe: ScreenerTimings["universe"] =
    universeRow?.detail?.startsWith("cached")
      ? "cached"
      : universeRow?.detail?.startsWith("rebuilt")
        ? "rebuilt"
        : "unknown";

  // Nested spans double-count against the bracket, so this can only ever be
  // reported as "at least this much was not attributed", never as a negative.
  const summed = stages.reduce((sum, s) => sum + s.ms, 0);
  const unattributedMs = Math.max(0, Math.round((totalMs - summed) * 10) / 10);

  return { stages, totalMs, unattributedMs, universe };
}

/** `1234.5` → `1.23s`, `12.3` → `12ms`. The panel reads at a glance or not at all. */
export function formatStageMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms >= 10) return `${Math.round(ms)}ms`;
  return `${ms.toFixed(1)}ms`;
}
